import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { User, ChevronRight } from 'lucide-react';
import { QUALITES } from './constants';
import SiretInput from '../ui/SiretInput';
import { formatCodeNAF } from '../../utils/codeNaf';
import CountrySelect from '../ui/CountrySelect';
import PhoneInput from '../ui/PhoneInput';

export default function IdentiteTab({ identite, setIdentite, operation: operation, isPersonneMorale, setActiveTab }) {
  // Callback d'auto-complétion SIRET → remplit raison sociale, NAF, adresse, CP, ville
  const handleSiretAutoComplete = (company) => {
    if (!company) return;
    // Helper : préserve la saisie utilisateur (ne remplace que si vide ou whitespace)
    const fill = (current, incoming) => (current && current.trim()) ? current : (incoming || '');
    setIdentite((prev) => ({
      ...prev,
      raison_sociale: fill(prev.raison_sociale, company.raison_sociale),
      nom: fill(prev.nom, company.raison_sociale),
      code_naf: fill(prev.code_naf, company.code_naf),
      forme_juridique: fill(prev.forme_juridique, company.forme_juridique),
      adresse: fill(prev.adresse, company.adresse),
      code_postal: fill(prev.code_postal, company.code_postal),
      ville: fill(prev.ville, company.ville),
    }));
  };
  return (
    <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="h-5 w-5 text-emerald-600" />
                    Qualité du souscripteur
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {QUALITES.map((q) => (
                      <button
                        key={q.value}
                        onClick={() => setIdentite(prev => ({ ...prev, qualite: q.value }))}
                        data-testid={`qualite-${q.value}`}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          identite.qualite === q.value
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <q.icon className={`h-6 w-6 mx-auto mb-2 ${
                          identite.qualite === q.value ? 'text-emerald-600' : 'text-gray-400'
                        }`} />
                        <div className="font-medium text-sm">{q.label}</div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Coordonnées</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!isPersonneMorale && (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Civilité</Label>
                        <Select 
                          value={identite.civilite} 
                          onValueChange={(v) => setIdentite(prev => ({ ...prev, civilite: v }))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Choisir" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M.">M.</SelectItem>
                            <SelectItem value="Mme">Mme</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Nom *</Label>
                        <Input
                          value={identite.nom}
                          onChange={(e) => setIdentite(prev => ({ ...prev, nom: e.target.value }))}
                          className="mt-1"
                          data-testid="input-nom"
                        />
                      </div>
                      <div>
                        <Label>Prénom</Label>
                        <Input
                          value={identite.prenom}
                          onChange={(e) => setIdentite(prev => ({ ...prev, prenom: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                  
                  {isPersonneMorale && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Raison sociale *</Label>
                          <Input
                            value={identite.raison_sociale}
                            onChange={(e) => setIdentite(prev => ({ ...prev, raison_sociale: e.target.value, nom: e.target.value }))}
                            className="mt-1"
                            data-testid="input-raison-sociale"
                          />
                        </div>
                        <div>
                          <Label>SIRET</Label>
                          <SiretInput
                            value={identite.siret || ''}
                            onChange={(value) => setIdentite(prev => ({ ...prev, siret: (value || '').replace(/\D/g, '').slice(0, 14) }))}
                            onAutoComplete={handleSiretAutoComplete}
                            showLabel={false}
                            placeholder="14 chiffres — auto-complétion"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Forme juridique</Label>
                          <Input
                            value={identite.forme_juridique}
                            onChange={(e) => setIdentite(prev => ({ ...prev, forme_juridique: e.target.value }))}
                            className="mt-1"
                            placeholder="Ex: SARL, SAS, SCI..."
                          />
                        </div>
                        <div>
                          <Label>Code NAF/APE</Label>
                          <Input
                            value={identite.code_naf}
                            onChange={(e) => setIdentite(prev => ({ ...prev, code_naf: e.target.value.toUpperCase() }))}
                            onBlur={(e) => setIdentite(prev => ({ ...prev, code_naf: formatCodeNAF(e.target.value) }))}
                            className="mt-1 font-mono"
                            placeholder="Ex: 41.20A"
                            maxLength={6}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Nom du représentant *</Label>
                          <Input
                            value={identite.representant_nom}
                            onChange={(e) => setIdentite(prev => ({ ...prev, representant_nom: e.target.value }))}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label>Qualité du représentant</Label>
                          <Input
                            value={identite.representant_qualite}
                            onChange={(e) => setIdentite(prev => ({ ...prev, representant_qualite: e.target.value }))}
                            className="mt-1"
                            placeholder="Ex: Gérant, Président, Directeur..."
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <Separator />
                  
                  <div>
                    <Label>Adresse *</Label>
                    <Input
                      value={identite.adresse}
                      onChange={(e) => setIdentite(prev => ({ ...prev, adresse: e.target.value }))}
                      className="mt-1"
                      data-testid="input-adresse"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>Code postal *</Label>
                      <Input
                        value={identite.code_postal}
                        onChange={(e) => setIdentite(prev => ({ ...prev, code_postal: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Ville *</Label>
                      <Input
                        value={identite.ville}
                        onChange={(e) => setIdentite(prev => ({ ...prev, ville: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Pays</Label>
                      <CountrySelect
                        value={identite.pays}
                        onChange={(v) => setIdentite(prev => ({ ...prev, pays: v }))}
                        showLabel={false}
                        placeholder="France"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Téléphone *</Label>
                      <PhoneInput
                        value={identite.telephone}
                        onChange={(v) => setIdentite(prev => ({ ...prev, telephone: v }))}
                        showLabel={false}
                        defaultCountryCode="GP"
                        placeholder="690 00 00 00"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        value={identite.email}
                        onChange={(e) => setIdentite(prev => ({ ...prev, email: e.target.value }))}
                        className="mt-1"
                        data-testid="input-email"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex justify-end">
                <Button onClick={() => setActiveTab('operation')} className="bg-emerald-600 hover:bg-emerald-700">
                  Suivant : Opération
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
    </div>
  );
}
